import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { FlashcardViewer } from '../components/Study/FlashcardViewer';
import { QuizMode } from '../components/Study/QuizMode';
import { FillInTheBlankGame } from '../components/Study/FillInTheBlankGame';
import { CreditCard, BookOpen, Target, Users, Clock, Star, FileText, CheckCircle, Edit3, ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface Document {
  fileName: string;
  fileSize: number;
  processedAt: string;
  studyContent?: {
    flashcards?: any[];
    questions?: any[];
  };
}

export const StudyPage: React.FC = () => {
  const { updateStudyStats, userProfile } = useAuth();
  const location = useLocation();
  const [activeMode, setActiveMode] = useState<'flashcards' | 'quiz' | 'fillInTheBlank'>('flashcards');
  const [studyContent, setStudyContent] = useState<any>(null);
  const [sessionSeconds, setSessionSeconds] = useState(0);
  const sessionSecondsRef = useRef(0);
  const [availableDocuments, setAvailableDocuments] = useState<Document[]>([]);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [showDocumentSelector, setShowDocumentSelector] = useState(true);

  useEffect(() => {
    // Load available documents from localStorage
    const docs = JSON.parse(localStorage.getItem('ecstatic_uploaded_documents') || '[]');
    setAvailableDocuments(docs);

    // Get study content from navigation state or auto-select first document
    const contentFromState = location.state?.studyContent;
    if (contentFromState) {
      setStudyContent(contentFromState[0]); // Use first document for now
      setSelectedDocument(contentFromState[0]);
      setShowDocumentSelector(false);
    } else if (docs.length > 0) {
      // Auto-select the first document if no specific content is provided
      setSelectedDocument(docs[0]);
      setStudyContent(docs[0]);
      setShowDocumentSelector(false);
    }
  }, [location]);

  // Study session timer
  useEffect(() => {
    const interval = setInterval(() => {
      setSessionSeconds((s) => {
        sessionSecondsRef.current = s + 1;
        return s + 1;
      });
    }, 1000);
    return () => {
      clearInterval(interval);
      // On unmount, increment studyMinutes by sessionSecondsRef.current/60 (rounded to nearest integer)
      if (sessionSecondsRef.current > 0 && userProfile) {
        const minutes = Math.round(sessionSecondsRef.current / 60);
        if (minutes > 0) {
          updateStudyStats({ studyMinutes: (userProfile.studyStats.studyMinutes || 0) + minutes });
        }
      }
    };
    // eslint-disable-next-line
  }, []);

  // Format session time
  const formatSessionTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m > 0 ? m + 'm ' : ''}${s}s`;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleDocumentSelect = (document: Document) => {
    setSelectedDocument(document);
    setStudyContent(document);
    setShowDocumentSelector(false);
  };

  const handleBackToDocumentSelector = () => {
    setShowDocumentSelector(true);
    setSelectedDocument(null);
    setStudyContent(null);
  };

  const studyModes = [
    {
      id: 'flashcards',
      title: 'Flashcards',
      description: 'Review terms and definitions',
      icon: CreditCard,
      color: 'bg-primary-600'
    },
    {
      id: 'quiz',
      title: 'Quiz Mode',
      description: 'Test your knowledge',
      icon: Target,
      color: 'bg-secondary-600'
    },
    {
      id: 'fillInTheBlank',
      title: 'Fill in the Blank',
      description: 'Complete definitions and facts',
      icon: Edit3,
      color: 'bg-accent-600'
    }
  ];

  const handleQuizComplete = (score: number, results: any[]) => {
    console.log('Quiz completed with score:', score, results);
    // TODO: Save results to Firebase
  };

  const handleFlashcardComplete = () => {
    console.log('Flashcard session completed');
    // TODO: Update user progress
  };

  const handleFillInTheBlankComplete = (score: number, timeSpent: number) => {
    console.log('Fill in the blank game completed with score:', score, 'time:', timeSpent);
    // TODO: Save results to Firebase
  };

  // Document Selection Interface - Compact
  if (showDocumentSelector) {
    return (
      <div className="h-full space-y-4">
        <div className="text-center">
          <h1 className="text-2xl font-heading font-bold text-gray-900 dark:text-white mb-2">
            Select Study Material
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Choose a document to study from
          </p>
        </div>

        {availableDocuments.length === 0 ? (
          <div className="text-center py-8">
            <BookOpen className="h-8 w-8 text-gray-400 mx-auto mb-3" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              No Study Materials Available
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Upload some documents first to generate study materials
            </p>
            <button
              onClick={() => window.location.href = '/upload'}
              className="bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
            >
              Upload Documents
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {availableDocuments.map((doc, index) => (
              <motion.div
                key={index}
                onClick={() => handleDocumentSelect(doc)}
                className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 cursor-pointer hover:border-primary-300 dark:hover:border-primary-600 transition-all hover:shadow-md"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-start space-x-3">
                  <div className="p-2 bg-primary-100 dark:bg-primary-900/50 rounded-lg">
                    <FileText className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-gray-900 dark:text-white truncate mb-1 text-sm">
                      {doc.fileName}
                    </h3>
                    <div className="space-y-0.5 text-xs text-gray-500 dark:text-gray-400">
                      <p>{formatFileSize(doc.fileSize)}</p>
                      <p>Uploaded: {new Date(doc.processedAt).toLocaleDateString()}</p>
                    </div>
                    <div className="mt-2 flex items-center space-x-3 text-xs">
                      <div className="flex items-center space-x-1">
                        <CreditCard className="h-3 w-3 text-primary-600" />
                        <span>{doc.studyContent?.flashcards?.length || 0} cards</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Target className="h-3 w-3 text-secondary-600" />
                        <span>{doc.studyContent?.questions?.length || 0} questions</span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (!studyContent || !studyContent.studyContent) {
    return (
      <div className="h-full space-y-4">
        <div className="text-center py-8">
          <BookOpen className="h-8 w-8 text-gray-400 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            No Study Materials Available
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Upload some documents first to generate study materials
          </p>
          <button
            onClick={() => window.location.href = '/upload'}
            className="bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
          >
            Upload Documents
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full space-y-4">
      {/* Header - Compact */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <button
            onClick={handleBackToDocumentSelector}
            className="flex items-center space-x-1 text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 transition-colors text-sm"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back</span>
          </button>
        </div>
        <div className="text-center">
          <h1 className="text-xl font-heading font-bold text-gray-900 dark:text-white">
            Study Session
          </h1>
          <p className="text-xs text-primary-600 dark:text-primary-400">
            Session time: {formatSessionTime(sessionSeconds)}
          </p>
        </div>
        <div className="w-16"></div> {/* Spacer for centering */}
      </div>

      {/* Study Mode Selection - Compact */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {studyModes.map((mode) => (
          <motion.button
            key={mode.id}
            onClick={() => setActiveMode(mode.id as any)}
            className={`p-4 rounded-lg border-2 transition-all ${
              activeMode === mode.id
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                : 'border-gray-200 dark:border-gray-700 hover:border-primary-300'
            }`}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className={`inline-flex p-2 rounded-lg text-white mb-3 ${mode.color}`}>
              <mode.icon className="h-4 w-4" />
            </div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
              {mode.title}
            </h3>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              {mode.description}
            </p>
          </motion.button>
        ))}
      </div>

      {/* Study Content Stats - Compact */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-2 mb-3">
          <FileText className="h-4 w-4 text-primary-600" />
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
            {studyContent.fileName}
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="text-center">
            <div className="text-lg font-bold text-primary-600 dark:text-primary-400">
              {studyContent.studyContent?.flashcards?.length || 0}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Flashcards</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-secondary-600 dark:text-secondary-400">
              {studyContent.studyContent?.questions?.length || 0}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Questions</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-accent-600 dark:text-accent-400">
              {Math.round(studyContent.fileSize / 1024)}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">KB</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-success-600 dark:text-success-400">
              ✓
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Ready</div>
          </div>
        </div>
      </div>

      {/* Active Study Mode */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 flex-1">
        {activeMode === 'flashcards' && (
          <FlashcardViewer
            flashcards={studyContent.studyContent?.flashcards || []}
            onComplete={handleFlashcardComplete}
          />
        )}
        {activeMode === 'quiz' && (
          <QuizMode
            questions={studyContent.studyContent?.questions || []}
            onComplete={handleQuizComplete}
          />
        )}
        {activeMode === 'fillInTheBlank' && (
          <FillInTheBlankGame
            flashcards={studyContent.studyContent?.flashcards || []}
            onComplete={handleFillInTheBlankComplete}
          />
        )}
      </div>
    </div>
  );
};